import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react'
import { useRequestUserConfigContext } from '../hooks/request'
import sessionClient from '../clients/session-client'
import { useAsyncCallback } from '../hooks/async'
import { AB_KEY } from '../common/constants'
import { RequestUserConfig } from '../common/types'
import { setABConfig } from '../utils/dom-utils'
import { ABConfigResponseTransport } from '../transports/ab-config-transport'

interface ABConfigContextType {
    readonly isVariant: (experimentName: AB_KEY, variant: number) => boolean
    readonly hasABConfigLoaded: boolean
    readonly isLoading: boolean
    readonly error: Error | null
}

const ABConfigContext = createContext<ABConfigContextType>({
    isVariant: () => false,
    hasABConfigLoaded: false,
    isLoading: false,
    error: null,
})

export const _getABConfig = async (user: RequestUserConfig) => {
    try {
        const response = await sessionClient.getABConfig({ user })
        if (response?.data) {
            return response.data
        } else {
            console.error('No AB config data received')
            return null
        }
    } catch (error) {
        console.error('Failed to fetch AB config', error)
        throw error
    }
}

export const ABConfigProvider = ({ children }: { children: ReactNode }) => {
    const user = useRequestUserConfigContext()
    const sessionId = user.session?.id

    const fetchABConfig = useCallback(() => _getABConfig(user), [user])

    const {
        data: abConfigData,
        callback: fetchABConfigCallback,
        error,
        isLoading,
    } = useAsyncCallback<ABConfigResponseTransport | null>(fetchABConfig)

    useEffect(() => {
        if (sessionId) {
            fetchABConfigCallback()
        }
    }, [sessionId])

    useEffect(() => {
        if (abConfigData) {
            setABConfig(abConfigData)
        }
    }, [abConfigData])

    const getVariant = useCallback(
        (experimentName: AB_KEY): number | undefined => abConfigData?.[experimentName]?.activeVariant,
        [abConfigData],
    )

    const isVariant = useCallback(
        (experimentName: AB_KEY, variant: number): boolean => getVariant(experimentName) === variant,
        [getVariant],
    )

    const contextValue = useMemo<ABConfigContextType>(
        () => ({
            isVariant,
            hasABConfigLoaded: !!abConfigData,
            isLoading,
            error,
        }),
        [isVariant, abConfigData, isLoading, error],
    )

    return <ABConfigContext.Provider value={contextValue}>{children}</ABConfigContext.Provider>
}

export const useABConfig = () => useContext(ABConfigContext)
